import { supabase } from "@/lib/supabase";
import {
  DEFAULT_DISCOVER_FILTERS,
  type DiscoverFilters,
  normalizeDiscoverFilters,
} from "@/lib/discoverFilters";

export type ProfileRow = {
  id: string;
  full_name: string | null;
  bio: string | null;
  location: string | null;
  photos: string[] | null;
  profile_completed: boolean | null;
  birthdate?: string | null;
  interests?: string[] | null;
  gender_identity?: string | null;
};

type MatchingPreferences = {
  showMeOnPride: boolean;
  prioritizeVerified: boolean;
  hideAlreadySeen: boolean;
};

type ViewerSafetyPreferences = {
  requireVerification: boolean;
};

const DEFAULT_MATCHING_PREFERENCES: MatchingPreferences = {
  showMeOnPride: true,
  prioritizeVerified: false,
  hideAlreadySeen: true,
};

type DiscoverProfileRowRaw = ProfileRow & {
  updated_at?: string | null;
  birthdate?: string | null;
  privacy_settings?: Record<string, any> | null;
  safety_settings?: Record<string, any> | null;
  lifestyle_interests?: Record<string, any> | null;
  vibe_categories?: string[] | null;
  connection_intent?: string[] | null;
  pronouns?: string | null;
};

const isTruthy = (value: unknown, fallback: boolean) =>
  typeof value === "boolean" ? value : fallback;

async function loadMyMatchingPreferences(myId: string): Promise<MatchingPreferences> {
  const { data, error } = await supabase
    .from("profiles")
    .select("privacy_settings")
    .eq("id", myId)
    .maybeSingle();

  if (error) {
    console.warn("Could not load matching preferences. Falling back to defaults.", error.message);
    return { ...DEFAULT_MATCHING_PREFERENCES };
  }

  const privacy =
    data?.privacy_settings && typeof data.privacy_settings === "object"
      ? (data.privacy_settings as Record<string, any>)
      : {};
  const matching =
    privacy.matching && typeof privacy.matching === "object"
      ? (privacy.matching as Record<string, any>)
      : {};

  return {
    showMeOnPride: isTruthy(matching.showMeOnPride, DEFAULT_MATCHING_PREFERENCES.showMeOnPride),
    prioritizeVerified: isTruthy(
      matching.prioritizeVerified,
      DEFAULT_MATCHING_PREFERENCES.prioritizeVerified
    ),
    hideAlreadySeen: isTruthy(matching.hideAlreadySeen, DEFAULT_MATCHING_PREFERENCES.hideAlreadySeen),
  };
}

async function loadMySafetyPreferences(myId: string): Promise<ViewerSafetyPreferences> {
  const { data, error } = await supabase
    .from("profiles")
    .select("safety_settings")
    .eq("id", myId)
    .maybeSingle();

  if (error) {
    console.warn("Could not load safety preferences. Falling back to defaults.", error.message);
    return { requireVerification: false };
  }

  const safety =
    data?.safety_settings && typeof data.safety_settings === "object"
      ? (data.safety_settings as Record<string, any>)
      : {};

  return {
    requireVerification: safety.requireVerification === true,
  };
}

async function loadMyDiscoverFilters(myId: string): Promise<DiscoverFilters> {
  const { data, error } = await supabase
    .from("profiles")
    .select("privacy_settings")
    .eq("id", myId)
    .maybeSingle();

  if (error) {
    console.warn("Could not load discover filters. Falling back to defaults.", error.message);
    return { ...DEFAULT_DISCOVER_FILTERS };
  }

  const privacy =
    data?.privacy_settings && typeof data.privacy_settings === "object"
      ? (data.privacy_settings as Record<string, any>)
      : {};

  return normalizeDiscoverFilters(privacy.discoverFilters);
}

async function loadSeenIds(myId: string): Promise<Set<string>> {
  const seenIds = new Set<string>();

  const { data: likeRows, error: likeError } = await supabase
    .from("likes")
    .select("liked_id")
    .eq("liker_id", myId);
  if (!likeError) {
    (likeRows ?? []).forEach((row: any) => {
      if (typeof row.liked_id === "string") seenIds.add(row.liked_id);
    });
  } else {
    console.warn("Could not load seen profiles from likes.", likeError.message);
  }

  const { data: matchRows, error: matchError } = await supabase
    .from("matches")
    .select("user1_id, user2_id")
    .or(`user1_id.eq.${myId},user2_id.eq.${myId}`);
  if (!matchError) {
    (matchRows ?? []).forEach((row: any) => {
      if (row.user1_id && row.user1_id !== myId) seenIds.add(row.user1_id);
      if (row.user2_id && row.user2_id !== myId) seenIds.add(row.user2_id);
    });
  } else {
    console.warn("Could not load seen profiles from matches.", matchError.message);
  }

  return seenIds;
}

function calcAgeFromBirthdate(birthdate?: string | null) {
  if (!birthdate) return null;
  const d = new Date(birthdate);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function normalizeToken(v: unknown) {
  return `${v ?? ""}`.trim().toLowerCase();
}

function flattenLifestyleStrings(lifestyle: Record<string, any> | null | undefined) {
  if (!lifestyle || typeof lifestyle !== "object") return [] as string[];

  const values: string[] = [];
  for (const raw of Object.values(lifestyle)) {
    if (Array.isArray(raw)) {
      raw.forEach((entry) => values.push(`${entry ?? ""}`));
    } else if (typeof raw === "string") {
      values.push(raw);
    }
  }
  return values;
}

function collectProfileInterestTokens(row: DiscoverProfileRowRaw) {
  const tokens = new Set<string>();

  const addToken = (value: string) => {
    const normalized = normalizeToken(value);
    if (!normalized) return;
    tokens.add(normalized);
  };

  (row.interests ?? []).forEach((entry) => {
    const raw = `${entry ?? ""}`.trim();
    if (!raw) return;
    addToken(raw);
    if (raw.includes(":")) {
      addToken(raw.split(":").slice(1).join(":"));
    }
  });

  flattenLifestyleStrings(row.lifestyle_interests).forEach((entry) => addToken(entry));
  return tokens;
}

function inferPronouns(row: DiscoverProfileRowRaw) {
  const pronouns = new Set<string>();

  const add = (value: string) => {
    const normalized = normalizeToken(value);
    if (!normalized) return;
    pronouns.add(normalized);
  };

  const identity = normalizeToken(row.gender_identity);
  if (identity.includes("woman") || identity.includes("female") || identity.includes("she")) {
    add("she/her");
  }
  if (
    identity.includes("non-binary") ||
    identity.includes("nonbinary") ||
    identity.includes("genderfluid") ||
    identity.includes("they")
  ) {
    add("they/them");
  }
  if (identity.includes("man") || identity.includes("male") || identity.includes("he")) {
    add("he/him");
  }

  flattenLifestyleStrings(row.lifestyle_interests).forEach((entry) => {
    const normalized = normalizeToken(entry);
    if (normalized.includes("she/her")) add("she/her");
    if (normalized.includes("they/them")) add("they/them");
    if (normalized.includes("he/him")) add("he/him");
  });

  return pronouns;
}

function deriveLookingForTags(row: DiscoverProfileRowRaw) {
  const tags = new Set<string>();
  const terms = collectProfileInterestTokens(row);

  const relationshipItems = Array.isArray(row.lifestyle_interests?.Relationship)
    ? (row.lifestyle_interests?.Relationship as string[])
    : [];
  const relationship = relationshipItems.map((entry) => normalizeToken(entry));

  if (
    relationship.some((r) =>
      ["exploring", "open to both", "polyamorous"].some((needle) => r.includes(needle))
    ) ||
    terms.has("dating")
  ) {
    tags.add("casual");
  }

  if (
    relationship.some((r) => r.includes("monogamous")) ||
    terms.has("relationship")
  ) {
    tags.add("serious");
  }

  if (
    ["support groups", "book clubs", "coffee dates", "queer meetups", "volunteering", "mentoring"].some(
      (v) => terms.has(v)
    )
  ) {
    tags.add("friends");
  }

  if (["networking", "community organizing", "entrepreneurship"].some((v) => terms.has(v))) {
    tags.add("networking");
  }

  return tags;
}

export async function fetchDiscoverProfiles(myId: string) {
  const matchingPrefs = await loadMyMatchingPreferences(myId);
  const safetyPrefs = await loadMySafetyPreferences(myId);
  const discoverFilters = await loadMyDiscoverFilters(myId);

  const { data, error } = await supabase
    .from("profiles")
    .select(
      "id, full_name, bio, location, photos, profile_completed, birthdate, interests, gender_identity, updated_at, privacy_settings, safety_settings, lifestyle_interests, vibe_categories, connection_intent, pronouns"
    )
    .neq("id", myId)
    .eq("profile_completed", true)
    .neq("is_seed", true)
    .order("updated_at", { ascending: false })
    .limit(120);

  if (error) throw error;

  let rows = (data ?? []) as DiscoverProfileRowRaw[];

  // Hide profiles with no photos — seed profiles never have photos and real
  // users can't complete onboarding without uploading at least one.
  rows = rows.filter((row) => Array.isArray(row.photos) && row.photos.length > 0);

  // Respect each user's discoverability toggle. Missing setting defaults to visible.
  rows = rows.filter((row) => {
    const privacy = row.privacy_settings && typeof row.privacy_settings === "object"
      ? (row.privacy_settings as Record<string, any>)
      : {};
    const matching = row.privacy_settings?.matching;
    if (privacy.profileDiscoverable === false) return false;
    if (privacy.hideFromSearch === true) return false;
    if (privacy.incognitoMode === true) return false;
    if (matching && typeof matching === "object" && matching.showMeOnPride === false) return false;
    return true;
  });

  rows = rows.filter((row) => {
    const age = calcAgeFromBirthdate(row.birthdate);
    const [minAge, maxAge] = discoverFilters.ageRange;
    if (age != null && (age < minAge || age > maxAge)) return false;

    const selectedInterests = discoverFilters.interests.map((v) => normalizeToken(v));
    if (selectedInterests.length > 0) {
      const availableTokens = collectProfileInterestTokens(row);
      const hasMatch = selectedInterests.some((selected) => availableTokens.has(selected));
      if (!hasMatch) return false;
    }

    const pronounFilters = discoverFilters.pronouns
      .filter((p) => p !== "Any")
      .map((p) => normalizeToken(p));
    if (pronounFilters.length > 0) {
      const profilePronouns = inferPronouns(row);
      const pronounMatch = pronounFilters.some((p) => profilePronouns.has(p));
      if (!pronounMatch) return false;
    }

    const lookingForFilters = discoverFilters.lookingFor.map((v) => normalizeToken(v));
    if (lookingForFilters.length > 0) {
      const profileTags = deriveLookingForTags(row);
      const lookingForMatch = lookingForFilters.some((selected) => profileTags.has(selected));
      if (!lookingForMatch) return false;
    }

    const distanceValue = Number(
      (row.lifestyle_interests as any)?.distance_miles ??
        (row.lifestyle_interests as any)?.distanceMiles ??
        NaN
    );
    if (Number.isFinite(distanceValue) && distanceValue > discoverFilters.distanceMiles) {
      return false;
    }

    return true;
  });

  // Filter by vibe categories (viewer has selected specific ones)
  const vibeCatFilters = discoverFilters.vibeCategories;
  if (vibeCatFilters.length > 0) {
    rows = rows.filter((row) => {
      const cats = Array.isArray(row.vibe_categories) ? row.vibe_categories : [];
      return vibeCatFilters.some((f) => cats.includes(f));
    });
  }

  // Filter by connection intent (must share at least one OR profile is open to everything)
  const intentFilters = discoverFilters.connectionIntent;
  if (intentFilters.length > 0) {
    rows = rows.filter((row) => {
      const intents = Array.isArray(row.connection_intent) ? row.connection_intent : [];
      return intents.includes("Open to Everything") || intentFilters.some((f) => intents.includes(f));
    });
  }

  if (safetyPrefs.requireVerification) {
    rows = rows.filter((row) => row.safety_settings?.photoVerification === true);
  }

  if (matchingPrefs.hideAlreadySeen) {
    const seenIds = await loadSeenIds(myId);
    rows = rows.filter((row) => !seenIds.has(row.id));
  }

  if (matchingPrefs.prioritizeVerified) {
    rows.sort((a, b) => {
      const aVerified = a.safety_settings?.photoVerification === true ? 1 : 0;
      const bVerified = b.safety_settings?.photoVerification === true ? 1 : 0;
      if (aVerified !== bVerified) return bVerified - aVerified;

      const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return bTime - aTime;
    });
  }

  return rows.slice(0, 50).map((row) => ({
    id: row.id,
    full_name: row.full_name,
    bio: row.bio,
    location: row.privacy_settings?.showDistance === false ? null : row.location,
    photos: row.photos,
    profile_completed: row.profile_completed,
    birthdate: row.privacy_settings?.showAge === false ? null : (row as any).birthdate ?? null,
    interests: (row as any).interests ?? [],
    gender_identity: (row as any).gender_identity ?? null,
  })) as ProfileRow[];
}
